import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconArrowRight,
  IconLoader,
  IconDownload,
  PermissionEnum,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  useAssignEmployeeToChatMutation,
  useReadMessagesMutation,
  useLazyGetChatDocumentFileQuery,
} from '../hoc/store/services';
import { Message } from '../hoc/types';
import formatСhatDate from '../hoc/helpers/format-chat-date';
import { setMessages } from '../hoc/store/slices/message';
import { NewMessageTypes } from './types';

import cn from './messages.module.css';

const NewMessage = ({
  messages,
  path,
  setAssignPanelIsOpen,
  userName,
  getEmployees,
  messageId,
  createdDate,
}: NewMessageTypes) => {
  const navigate = useNavigate();
  const [unreadMessages, setUnreadMessages] = useState<Message[]>([]);
  const [selectedDocumentId, setSelectedDocumentId] = useState('');

  const {
    messagesDictionaries: { activePermissions },
    messages: {
      general: { employee },
    },
  } = useAppSelector((state) => state);

  const [assignToChat, { isLoading, isSuccess }] =
    useAssignEmployeeToChatMutation();
  const [readMessages] = useReadMessagesMutation();
  const [getChatDocumentFile, { isLoading: downloadInProgress, isFetching }] =
    useLazyGetChatDocumentFileQuery();

  const dispatch = useAppDispatch();

  const getFile = async (documentId: string, documentName: string) => {
    setSelectedDocumentId(documentId);

    const response = await getChatDocumentFile({
      id: messageId as string,
      documentId,
    });

    if (response && response.data) {
      const hiddenElement = document.createElement('a');
      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      hiddenElement.href = blob;
      hiddenElement.target = '_blank';
      hiddenElement.download = documentName;
      hiddenElement.click();

      setSelectedDocumentId('');
    }
  };

  const assignToChatAction = () => {
    assignToChat({
      id: messageId as string,
      body: {
        employeeId: employee?.id as string,
      },
    });
  };

  useEffect(() => {
    if (isSuccess) {
      navigate(path);
    }
  }, [isSuccess]);

  useEffect(() => {
    if (messages && messages.length) {
      const unreadedMessages = messages?.filter((item) => !item.readAt);

      if (unreadedMessages?.length > 0) {
        setUnreadMessages(unreadedMessages);
      }
    }
  }, [messages]);

  useEffect(() => {
    if (messages?.find((item) => !item.readAt) && messageId && createdDate) {
      readMessages({
        id: messageId,
        body: {
          // createdAtFrom: createdDate,
          // createdAtTo: new Date().toISOString(),
          messages: messages
            ?.filter((item) => !item.readAt)
            ?.map((item) => item.id) as string[],
          channelId: messageId,
        },
      });

      const nextMessages = messages.map((item) => ({
        ...item,
        readAt: new Date().toISOString(),
      }));

      dispatch(setMessages(nextMessages));
    }
  }, [messages, messageId, createdDate]);

  return (
    <div className={cn.newMessageContent}>
      <div className={cn.newMessageContentUser}>{userName}</div>
      {unreadMessages?.map((item) => {
        const date = new Date(item.createdAt);

        let dateString;

        if (new Date().toLocaleDateString() === date.toLocaleDateString()) {
          dateString = `${date.getHours().toString().padStart(2, '0')}:${date
            .getMinutes()
            .toString()
            .padStart(2, '0')}`;
        } else {
          dateString = formatСhatDate(item.createdAt, true);
        }

        return (
          <div className={cn.messageBlock} key={item.id}>
            <span className={cn.messageBlockContent}>
              <div className={cn.messageContent}>
                {item.document && (
                  <div
                    className={`${cn.messageAttachment} ${
                      item.content ? cn.messageAttachmentMargin : ''
                    }`}
                  >
                    <button
                      type="button"
                      className={cn.messageAttachmentLink}
                      aria-label="Download link"
                      onClick={() =>
                        getFile(
                          item?.documentId as string,
                          item.document?.name as string,
                        )
                      }
                    >
                      {(downloadInProgress || isFetching) &&
                      selectedDocumentId === item.documentId ? (
                        <IconLoader width={20} height={20} />
                      ) : (
                        <IconDownload />
                      )}
                    </button>
                    <div className={cn.messageAttachmentName}>
                      {item.document.name}
                    </div>
                  </div>
                )}
                {item.content}
              </div>
              <div className={cn.messageDate}>{dateString}</div>
            </span>
          </div>
        );
      })}
      <div className={cn.messageControls}>
        {activePermissions?.includes(
          PermissionEnum.MESSAGES_ASSIGN_CHAT_TO_ME_ACTION,
        ) && (
          <button
            type="button"
            className={cn.startChatButton}
            onClick={() => {
              assignToChatAction();
            }}
          >
            {isLoading ? 'Loading...' : 'Start Chat'}
          </button>
        )}
        {activePermissions?.includes(
          PermissionEnum.MESSAGES_ASSIGN_CHAT_TO_ANOTHER_ACTION,
        ) && (
          <button
            type="button"
            className={cn.assignUserButton}
            onClick={() => {
              setAssignPanelIsOpen(true);
              getEmployees();
            }}
          >
            Assign
            <IconArrowRight />
          </button>
        )}
      </div>
    </div>
  );
};

export default NewMessage;
